import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Spin, Typography, message, Form, Select, Input, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { liveFollowAPI, tradeAPI, notificationAPI, brokerAPI, authAPI } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

function Field({ label, value }) {
  const v = value === null || value === undefined || value === '' ? '-' : String(value);
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
      <div style={{ width: 80, color: '#666', flex: '0 0 auto' }}>{label}</div>
      <div style={{ color: '#111', flex: 1, wordBreak: 'break-all' }}>{v}</div>
    </div>
  );
}

export default function MobileMasterDataLiveFollows() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [topics, setTopics] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [startingSyncId, setStartingSyncId] = useState(null);
  const [startingNotifyId, setStartingNotifyId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const [liveFollowsResp, topicsResp, webhooksResp, usersResp] = await Promise.all([liveFollowAPI.list(), tradeAPI.getTopics(), notificationAPI.getWebhooks(), authAPI.users()]);
      const itemsData = Array.isArray(liveFollowsResp?.data) ? liveFollowsResp.data : [];
      // 优先显示 IB 账户（排序：ib 在前，其他在后）
      const sortedItems = [...itemsData].sort((a, b) => {
        const aIsIb = (a?.broker_type || '').toLowerCase() === 'ib';
        const bIsIb = (b?.broker_type || '').toLowerCase() === 'ib';
        if (aIsIb && !bIsIb) return -1;
        if (!aIsIb && bIsIb) return 1;
        return 0;
      });
      setItems(sortedItems);
      setTopics(Array.isArray(topicsResp?.data) ? topicsResp.data : []);
      setWebhooks(Array.isArray(webhooksResp?.data) ? webhooksResp.data : []);
      setUsers(Array.isArray(usersResp?.data) ? usersResp.data : []);

      // 获取所有券商类型的账户
      const brokerTypes = ['longport', 'ib', 'tiger', 'futu', 'moomoo'];
      const accountPromises = brokerTypes.map((type) => {
        const apiKey = {
          longport: 'long_port',
          ib: 'ib',
          tiger: 'tiger',
          futu: 'futu',
          moomoo: 'moomoo',
        }[type];
        return brokerAPI.list(apiKey).catch(() => ({ data: [] }));
      });

      const accountResponses = await Promise.all(accountPromises);
      const allAccounts = accountResponses.flatMap((resp) => (Array.isArray(resp?.data) ? resp.data : []));
      setAccounts(allAccounts);
    } catch (e) {
      message.error('获取列表失败');
      setItems([]);
      setTopics([]);
      setWebhooks([]);
      setUsers([]);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleBack = () => {
    navigate('/master-data', { replace: true });
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t?.id === topicId);
    return topic ? `${topicId} - ${topic.name}` : String(topicId || '');
  };

  const getWebhookName = (webhookId) => {
    const webhook = webhooks.find((w) => w?.id === webhookId);
    return webhook ? `${webhookId} - ${webhook.name || webhook.id}` : String(webhookId || '');
  };

  const getUsername = (userId) => {
    const user = users.find((u) => u?.id === userId);
    return user ? `${userId} - ${user.username}` : String(userId || '');
  };

  const getAccountName = (accountId, brokerType) => {
    // 先尝试同时匹配 accountId 和 brokerType
    let account = accounts.find((a) => a?.id === accountId && (a?.broker_type || '').toLowerCase() === (brokerType || '').toLowerCase());
    // 如果找不到，回退到只按 accountId 匹配
    if (!account) {
      account = accounts.find((a) => a?.id === accountId);
    }
    return account ? `${accountId} - ${account.name || account.id}` : String(accountId || '');
  };

  const formatFundLimit = (type, value) => {
    if (!type || value === null || value === undefined || value === '') return '';
    const t = type.toLowerCase();
    if (t === 'ratio') return `比例 ${value}`;
    if (t === 'usd') return `USD ${value}`;
    return `${type} ${value}`;
  };

  const formatLastSync = (date) => {
    return date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  const handleStartSync = async (record) => {
    if (!record?.id) return;
    setStartingSyncId(record.id);
    try {
      const res = await liveFollowAPI.startSync(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已启动同步');
      } else {
        message.error('启动同步失败');
      }
      Modal.info({
        title: '启动同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchList();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('启动同步失败');
      Modal.info({
        title: '启动同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingSyncId(null);
    }
  };

  const handleStopSync = async (record) => {
    if (!record?.id) return;
    setStartingSyncId(record.id);
    try {
      const res = await liveFollowAPI.stopSync(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已关闭同步');
      } else {
        message.error('关闭同步失败');
      }
      Modal.info({
        title: '关闭同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchList();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('关闭同步失败');
      Modal.info({
        title: '关闭同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingSyncId(null);
    }
  };

  const handleStartNotify = async (record) => {
    if (!record?.id) return;
    setStartingNotifyId(record.id);
    try {
      const res = await liveFollowAPI.startNotify(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已启动通知');
      } else {
        message.error('启动通知失败');
      }
      Modal.info({
        title: '启动通知返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchList();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('启动通知失败');
      Modal.info({
        title: '启动通知返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingNotifyId(null);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      topic_id: record?.topic_id,
      fund_limit_type: record?.fund_limit_type || 'ratio',
      fund_limit_value: record?.fund_limit_value,
    });
    setEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      if (!editingRecord?.id) return;

      setEditLoading(true);
      const updateData = {
        topic_id: values.topic_id,
        fund_limit_type: values.fund_limit_type,
        fund_limit_value: values.fund_limit_value ? parseFloat(values.fund_limit_value) : null,
      };

      const res = await liveFollowAPI.update(editingRecord.id, updateData);
      if (res?.data) {
        message.success('修改成功');
        setEditModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchList();
      } else {
        message.error('修改失败');
      }
    } catch (e) {
      message.error(e?.message || '修改失败');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px 16px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
            实盘跟随
          </Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={fetchList}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              刷新
            </Button>
            <Button
              size="small"
              onClick={handleBack}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              返回
            </Button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin />
          </div>
        ) : (
          <div>
            {(items || []).map((row, idx) => (
              <Card key={row?.id ?? idx} style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: '#1677ff' }}>{row?.alias || `记录 ${idx + 1}`}</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button size="small" onClick={() => handleEdit(row)}>
                      编辑
                    </Button>
                    <Button size="small" type={row?.enabled ? 'primary' : 'default'} danger={!!row?.enabled} loading={startingSyncId === row.id} onClick={() => (row?.enabled ? handleStopSync(row) : handleStartSync(row))}>
                      {row?.enabled ? '关闭同步' : '启动同步'}
                    </Button>
                    <Button size="small" type={row?.notification_enabled ? 'primary' : 'default'} danger={!!row?.notification_enabled} loading={startingNotifyId === row.id} onClick={() => (row?.notification_enabled ? handleStopNotify(row) : handleStartNotify(row))}>
                      {row?.notification_enabled ? '关闭通知' : '启动通知'}
                    </Button>
                  </div>
                </div>
                <Field label="绑定用户" value={getUsername(row?.user_id)} />
                <Field label="别名" value={row?.alias} />
                <Field label="券商" value={row?.broker_type} />
                <Field label="账户" value={getAccountName(row?.broker_account_id, row?.broker_type)} />
                <Field label="话题" value={getTopicName(row?.topic_id)} />
                <Field label="启用" value={row?.enabled ? '是' : '否'} />
                <Field label="允许误差" value={row?.allowed_error} />
                <Field label="资金限制" value={formatFundLimit(row?.fund_limit_type, row?.fund_limit_value)} />
                <Field label="通知" value={row?.notification_enabled ? '是' : '否'} />
                <Field label="Webhook" value={getWebhookName(row?.webhook_id)} />
                <Field label="最后同步" value={formatLastSync(row?.last_sync_at)} />
                <Field label="同步状态" value={row?.last_sync_status} />
              </Card>
            ))}

            {!items || items.length === 0 ? (
              <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 12 } }}>
                <Text type="secondary">暂无数据</Text>
              </Card>
            ) : null}
          </div>
        )}
      </div>

      <Modal title="编辑实盘跟随" open={editModalVisible} onCancel={handleEditCancel} onOk={handleEditSave} confirmLoading={editLoading} width={360} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="追随话题" name="topic_id" rules={[{ required: true, message: '请选择话题' }]}>
            <Select placeholder="选择话题">
              {topics.map((topic) => (
                <Option key={topic.id} value={topic.id}>
                  {topic.id} - {topic.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="资金限制类型" name="fund_limit_type" rules={[{ required: true, message: '请选择资金限制类型' }]}>
            <Select placeholder="选择类型">
              <Option value="ratio">比例</Option>
              <Option value="usd">USD</Option>
            </Select>
          </Form.Item>

          <Form.Item label="资金限制值" name="fund_limit_value" rules={[{ required: true, message: '请输入资金限制值' }]}>
            <Input type="number" placeholder="例如: 0.95 或 10000" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
